import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot } from '../styles/iosNativePageCss';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAWSAuth as useAuth } from '../contexts/AWSAuthContext';
import { Mail, CheckCircle, AlertCircle, Loader, ArrowRight } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;

  ${iosNativePageRoot}
`;

const VerifyContainer = styled.div`
  width: 100%;
  max-width: 450px;
  animation: slideUp 0.5s ease-out;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    font-family: ${theme.typography.fontFamily.secondary};
    font-size: ${theme.typography.fontSize['4xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary[600]};
    margin: 0;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    margin-top: 0.5rem;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${theme.colors.primary[100]};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: ${theme.colors.primary[600]};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;

const CodeInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
`;

const CodeInput = styled.input<{ $hasError?: boolean }>`
  width: 3rem;
  height: 3.5rem;
  text-align: center;
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  border: 2px solid ${props => props.$hasError ? theme.colors.status.error : theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[400]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}30;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  
  svg {
    flex-shrink: 0;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.status.success}10;
  border: 1px solid ${theme.colors.status.success}30;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.status.success};
  font-size: ${theme.typography.fontSize.sm};
  
  svg {
    flex-shrink: 0;
  }
`;

const InfoText = styled.p`
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  margin: 0;
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary[600]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  
  &:hover {
    color: ${theme.colors.primary[700]};
  }
  
  &:disabled {
    color: ${theme.colors.text.tertiary};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const VerifyEmailPage: React.FC = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const { confirmSignUp, resendConfirmationCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { email?: string } | undefined;
  
  const [email, setEmail] = useState<string>(() => {
    return locationState?.email || localStorage.getItem('pendingVerificationEmail') || '';
  });

  useEffect(() => {
    if (locationState?.email) {
      setEmail(locationState.email);
      localStorage.setItem('pendingVerificationEmail', locationState.email);
    }
  }, [locationState?.email]);

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-focus first input
  useEffect(() => {
    const firstInput = document.getElementById('code-0');
    if (firstInput) {
      firstInput.focus();
    }
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Auto-submit when all digits entered
    if (index === 5 && value) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    
    setCode(newCode);
    
    // Focus last filled input
    const lastIndex = Math.min(pastedData.length, 5);
    const lastInput = document.getElementById(`code-${lastIndex}`);
    if (lastInput) {
      lastInput.focus();
    }

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const fullCode = verificationCode || code.join('');
    
    if (fullCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmSignUp(email, fullCode);
      setSuccess(true);
      localStorage.removeItem('pendingVerificationEmail');
      
      // Redirect to sign in after success
      setTimeout(() => {
        navigate('/signin', { 
          state: { 
            message: 'Email verified! You can now sign in.',
            email 
          } 
        });
      }, 2000);
    } catch (error: any) {
      console.error('Verification error:', error);
      
      const errorMessage = error.message || '';
      
      if (errorMessage.includes('CodeMismatchException') || errorMessage.includes('Invalid verification code')) {
        setError('Invalid code. Please check and try again.');
      } else if (errorMessage.includes('ExpiredCodeException') || errorMessage.includes('expired')) {
        setError('Code expired. Please request a new one.');
      } else if (errorMessage.includes('LimitExceededException')) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');

    try {
      await resendConfirmationCode(email);
      setResendCooldown(60); // 60 second cooldown
      setCode(['', '', '', '', '', '']); // Clear code inputs
      
      // Focus first input
      const firstInput = document.getElementById('code-0');
      if (firstInput) {
        firstInput.focus();
      }
    } catch (error: any) {
      console.error('Resend error:', error);
      setError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerify();
  };

  if (!email) {
    return null;
  }

  return (
    <PageContainer>
      <VerifyContainer>
        <Logo>
          <h1>GlowLens™</h1>
          <p>Verify your email to continue ✨</p>
        </Logo>

        <Card padding="lg">
          {success ? (
            <>
              <IconWrapper>
                <CheckCircle size={40} />
              </IconWrapper>
              <SuccessMessage>
                <CheckCircle size={20} />
                <span>Email verified successfully! Redirecting...</span>
              </SuccessMessage>
            </>
          ) : (
            <>
              <IconWrapper>
                <Mail size={40} />
              </IconWrapper>

              <InfoText style={{ marginBottom: '1.5rem' }}>
                We sent a 6-digit code to<br />
                <strong>{email}</strong>
              </InfoText>

              <Form onSubmit={handleSubmit}>
                <InputGroup>
                  <Label>Verification Code</Label>
                  <CodeInputContainer>
                    {code.map((digit, index) => (
                      <CodeInput
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        $hasError={!!error}
                        disabled={loading || success}
                      />
                    ))}
                  </CodeInputContainer>
                </InputGroup>

                {error && (
                  <ErrorMessage>
                    <AlertCircle size={20} />
                    <span>{error}</span>
                  </ErrorMessage>
                )}

                <Button 
                  type="submit" 
                  size="lg" 
                  fullWidth 
                  loading={loading}
                  disabled={code.join('').length !== 6 || success}
                >
                  {loading ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Verify Email
                      <ArrowRight size={20} />
                    </>
                  )}
                </Button>

                <InfoText>
                  Didn't receive the code?{' '}
                  <ResendLink
                    type="button"
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                  >
                    {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                  </ResendLink>
                </InfoText>
              </Form>
            </>
          )}
        </Card>
      </VerifyContainer>
    </PageContainer>
  );
};

export default VerifyEmailPage;



